import { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  MenuItem,
  Alert,
} from "@mui/material";

import SendRoundedIcon from "@mui/icons-material/SendRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import { Section, SectionHeading } from "../section/section";

const TOPICS = [
  "General inquiry",
  "Book a demo",
  "Sales & pricing",
  "Technical support",
  "Partnership",
];

const INFO = [
  {
    icon: EmailRoundedIcon,
    label: "Email us",
    value: "info@ytctechventures.com",
  },
  {
    icon: ChatRoundedIcon,
    label: "Sales",
    value: "sales@ytctechventures.com",
  },
  {
    icon: BusinessRoundedIcon,
    label: "Company",
    value: "YTCTechventures",
  },
];

function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    topic: "General inquiry",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};

    if (!form.name.trim()) next.name = "Please enter your name";

    if (!form.email.trim()) {
      next.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = "Please enter a valid email";
    }

    if (!form.message.trim()) {
      next.message = "Please tell us how we can help";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitted(true);

    setForm({
      name: "",
      email: "",
      company: "",
      topic: "General inquiry",
      message: "",
    });
  };

  return (
    <Section
      id="contact"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 10, md: 16 },

        background: `
          radial-gradient(circle at top left, rgba(59,130,246,.15), transparent 30%),
          radial-gradient(circle at bottom right, rgba(14,165,233,.12), transparent 35%),
          linear-gradient(180deg,#020617 0%,#0f172a 45%,#111827 100%)
        `,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -150,
          left: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "#2563eb",
          filter: "blur(140px)",
          opacity: 0.12,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: -180,
          right: -180,
          width: 450,
          height: 450,
          borderRadius: "50%",
          background: "#06b6d4",
          filter: "blur(160px)",
          opacity: 0.1,
        }}
      />

      <SectionHeading
        eyebrow="Contact Us"
        title="Let's talk about your operations"
        subtitle="Have a question or want a personalized demo? Send us a message and our team will get back to you."
        sx={{
          "& .eyebrow": { color: "#60a5fa" },
          "& .title": { color: "#f8fafc" },
          "& .subtitle": { color: "rgba(248,250,252,.72)" },
        }}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr" },
          gap: { xs: 3, md: 5 },
          maxWidth: 980,
          mx: "auto",
        }}
      >
        <Box>
          <Typography sx={{ mb: 2, color: "#f8fafc", fontWeight: 600 }}>
            Get in touch
          </Typography>

          <Typography sx={{ color: "rgba(248,250,252,.7)", mb: 3 }}>
            We typically respond within one business day.
          </Typography>
<Stack spacing={2} alignItems="flex-start">
  {INFO.map((item) => {
    const Icon = item.icon;

    return (
      <Box
        key={item.label}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.75,
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(79,107,237,0.14)",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: "#60a5fa", fontSize: 21 }} />
        </Box>

        <Box sx={{ textAlign: "left" }}>
          <Typography
            sx={{
              color: "rgba(248,250,252,.6)",
              fontSize: 12,
            }}
          >
            {item.label}
          </Typography>

          <Typography
            sx={{
              color: "#f8fafc",
              fontWeight: 700,
            }}
          >
            {item.value}
          </Typography>
        </Box>
      </Box>
    );
  })}
</Stack>
        </Box>

        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: 4,
            background: "rgba(17,24,39,0.6)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {submitted && (
            <Alert
              severity="success"
              sx={{ mb: 3 }}
              onClose={() => setSubmitted(false)}
            >
              Thanks for reaching out. We'll be in touch shortly.
            </Alert>
          )}

          <Stack spacing={2.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
              <TextField
                label="Full name"
                value={form.name}
                onChange={update("name")}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
                required
                sx={fieldStyles}
              />

              <TextField
                label="Email"
                value={form.email}
                onChange={update("email")}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
                required
                sx={fieldStyles}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
              <TextField
                label="Company"
                value={form.company}
                onChange={update("company")}
                fullWidth
                sx={fieldStyles}
              />

              <TextField
                select
                label="Topic"
                value={form.topic}
                onChange={update("topic")}
                fullWidth
                sx={fieldStyles}
              >
                {TOPICS.map((topic) => (
                  <MenuItem key={topic} value={topic}>
                    {topic}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <TextField
              label="How can we help?"
              value={form.message}
              onChange={update("message")}
              error={!!errors.message}
              helperText={errors.message}
              fullWidth
              multiline
              minRows={4}
              required
              sx={fieldStyles}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              endIcon={<SendRoundedIcon />}
              sx={{
                alignSelf: "flex-start",
                background: "#2563eb",
                "&:hover": { background: "#1d4ed8" },
              }}
            >
              Send Message
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Section>
  );
}

const fieldStyles = {
  "& .MuiInputBase-input": {
    color: "#f8fafc",
  },

  "& .MuiInputLabel-root": {
    color: "rgba(248,250,252,.7)",
  },

  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.25)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#60a5fa",
    },
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#60a5fa",
  },
};

export default Contact;